import { Assets, Texture, Sprite, Container, Graphics, Text, TextStyle } from 'pixi.js';

// Sprite paths - using Kenney Fish Pack (CC0 licensed)
const SPRITE_BASE = '/sprites/kenney/PNG/Double';

// Sprite definitions
export const SPRITES = {
  // Fish (food)
  fish_blue: `${SPRITE_BASE}/fish_blue.png`,
  fish_orange: `${SPRITE_BASE}/fish_orange.png`,
  fish_green: `${SPRITE_BASE}/fish_green.png`,
  fish_pink: `${SPRITE_BASE}/fish_pink.png`,
  fish_red: `${SPRITE_BASE}/fish_red.png`,
  fish_brown: `${SPRITE_BASE}/fish_brown.png`,
  fish_grey: `${SPRITE_BASE}/fish_grey.png`,

  // Obstacles
  rock_a: `${SPRITE_BASE}/rock_a.png`,
  rock_b: `${SPRITE_BASE}/rock_b.png`,

  // Environment
  seaweed_green: `${SPRITE_BASE}/seaweed_green_a.png`,
  bubble: `${SPRITE_BASE}/bubble_a.png`,
};

// Texture cache
const textureCache = new Map<string, Texture>();

// Load a texture with caching
export async function loadTexture(path: string): Promise<Texture> {
  if (textureCache.has(path)) {
    return textureCache.get(path)!;
  }

  try {
    const texture = await Assets.load<Texture>(path);
    textureCache.set(path, texture);
    return texture;
  } catch (error) {
    console.warn(`Failed to load texture: ${path}`, error);
    return Texture.WHITE;
  }
}

// Preload all game sprites
export async function preloadSprites(): Promise<void> {
  const paths = Object.values(SPRITES);
  await Promise.all(paths.map(loadTexture));
}

// Create a sprite from a loaded texture path
export async function createSpriteFromPath(path: string, scale = 1): Promise<Sprite> {
  const texture = await loadTexture(path);
  const sprite = new Sprite(texture);
  sprite.scale.set(scale);
  return sprite;
}

// Create an otter sprite (using emoji text for now - we can replace with real sprite later)
export function createOtterSprite(): Container {
  const container = new Container();

  // Create otter using graphics with better shape
  const body = new Graphics();

  // Main body - elongated oval
  body.ellipse(0, 0, 35, 18);
  body.fill(0x8B6914); // Brown fur color

  // Head
  body.ellipse(30, -2, 14, 12);
  body.fill(0x8B6914);

  // Belly (lighter)
  body.ellipse(-5, 5, 25, 10);
  body.fill(0xD4A54A);

  // Tail
  body.ellipse(-40, 2, 18, 6);
  body.fill(0x6B4E0A);

  // Nose
  body.circle(42, 0, 4);
  body.fill(0x2D1B0E);

  // Eye
  body.circle(35, -5, 3);
  body.fill(0x000000);
  body.circle(36, -6, 1);
  body.fill(0xFFFFFF);

  // Whiskers
  body.moveTo(42, 0);
  body.lineTo(52, -3);
  body.moveTo(42, 0);
  body.lineTo(52, 3);
  body.moveTo(42, 2);
  body.lineTo(50, 6);
  body.stroke({ width: 1, color: 0x4A3510 });

  // Little ear
  body.ellipse(28, -12, 4, 5);
  body.fill(0x8B6914);

  // Front paw
  body.ellipse(15, 15, 8, 5);
  body.fill(0x6B4E0A);

  // Back paw (webbed foot visible)
  body.ellipse(-25, 12, 10, 6);
  body.fill(0x6B4E0A);

  container.addChild(body);

  return container;
}

// Food sprite types
type FoodType = 'fish' | 'crab' | 'urchin' | 'shrimp' | 'clam' | 'default';

// Create food sprite using real images
export async function createFoodSprite(type: FoodType): Promise<Container> {
  const container = new Container();

  // Map food types to sprite paths
  const spriteMap: Record<FoodType, string> = {
    fish: SPRITES.fish_blue,
    crab: SPRITES.fish_orange, // Use orange fish for crab-like appearance
    urchin: SPRITES.fish_pink, // Use pink fish for urchin
    shrimp: SPRITES.fish_red,
    clam: SPRITES.fish_grey,
    default: SPRITES.fish_green,
  };

  const spritePath = spriteMap[type] || spriteMap.default;

  try {
    const sprite = await createSpriteFromPath(spritePath, 0.6);
    sprite.anchor.set(0.5);
    container.addChild(sprite);
  } catch {
    // Fallback to colored shape
    const fallback = new Graphics();
    fallback.circle(0, 0, 15);
    fallback.fill(type === 'fish' ? 0x4169E1 : type === 'crab' ? 0xFF6347 : 0xFFD700);
    container.addChild(fallback);
  }

  return container;
}

// Obstacle sprite types
type ObstacleType = 'rock' | 'log' | 'net' | 'shark' | 'pollution' | 'default';

// Create obstacle sprite
export async function createObstacleSprite(type: ObstacleType): Promise<Container> {
  const container = new Container();

  if (type === 'rock' || type === 'log') {
    // Use Kenney rock sprites
    const spritePath = Math.random() > 0.5 ? SPRITES.rock_a : SPRITES.rock_b;
    try {
      const sprite = await createSpriteFromPath(spritePath, type === 'rock' ? 0.8 : 1.2);
      sprite.anchor.set(0.5);
      if (type === 'log') {
        sprite.rotation = Math.PI / 2; // Rotate for log look
        sprite.tint = 0x8B4513; // Brown tint
      }
      container.addChild(sprite);
    } catch {
      // Fallback
      const fallback = new Graphics();
      fallback.roundRect(-25, -20, 50, 40, 8);
      fallback.fill(type === 'rock' ? 0x696969 : 0x8B4513);
      container.addChild(fallback);
    }
  } else if (type === 'shark') {
    // Draw a menacing shark shape - FACING LEFT (toward otter)
    const shark = new Graphics();

    // Body (reversed - nose on left)
    shark.moveTo(40, 0);
    shark.bezierCurveTo(30, -20, -30, -15, -45, 0);
    shark.bezierCurveTo(-30, 15, 30, 20, 40, 0);
    shark.fill(0x4A5568);

    // Dorsal fin
    shark.moveTo(0, -15);
    shark.lineTo(-10, -35);
    shark.lineTo(-20, -15);
    shark.fill(0x4A5568);

    // Tail fin (on right now)
    shark.moveTo(40, 0);
    shark.lineTo(55, -15);
    shark.lineTo(50, 0);
    shark.lineTo(55, 15);
    shark.fill(0x4A5568);

    // Eye (menacing) - on left side now
    shark.circle(-30, -5, 4);
    shark.fill(0x000000);
    shark.circle(-31, -6, 1.5);
    shark.fill(0xFF0000);

    // Teeth - on left side now
    for (let i = 0; i < 5; i++) {
      shark.moveTo(-35 - i * 3, 5);
      shark.lineTo(-36.5 - i * 3, 10);
      shark.lineTo(-38 - i * 3, 5);
    }
    shark.fill(0xFFFFFF);

    // Gills - moved to right side of body
    shark.moveTo(-15, -5);
    shark.lineTo(-15, 5);
    shark.moveTo(-10, -5);
    shark.lineTo(-10, 5);
    shark.moveTo(-5, -5);
    shark.lineTo(-5, 5);
    shark.stroke({ width: 1, color: 0x2D3748 });

    container.addChild(shark);
  } else if (type === 'net') {
    // Draw fishing net
    const net = new Graphics();
    const netColor = 0x228B22;

    // Net grid
    for (let x = -30; x <= 30; x += 10) {
      net.moveTo(x, -25);
      net.lineTo(x, 25);
    }
    for (let y = -25; y <= 25; y += 10) {
      net.moveTo(-30, y);
      net.lineTo(30, y);
    }
    net.stroke({ width: 2, color: netColor, alpha: 0.8 });

    // Border
    net.roundRect(-32, -27, 64, 54, 5);
    net.stroke({ width: 3, color: netColor });

    container.addChild(net);
  } else if (type === 'pollution') {
    // Draw pollution/trash
    const pollution = new Graphics();

    // Plastic bag shape
    pollution.roundRect(-15, -20, 30, 40, 5);
    pollution.fill({ color: 0x2F4F4F, alpha: 0.7 });

    // Some detail lines
    pollution.moveTo(-10, -15);
    pollution.lineTo(-10, 15);
    pollution.moveTo(0, -18);
    pollution.lineTo(0, 18);
    pollution.stroke({ width: 1, color: 0x1A1A1A, alpha: 0.5 });

    container.addChild(pollution);
  } else {
    // Default obstacle
    const fallback = new Graphics();
    fallback.roundRect(-20, -20, 40, 40, 5);
    fallback.fill(0x8B0000);
    container.addChild(fallback);
  }

  return container;
}

// Power-up sprite types
type PowerUpType = 'raft' | 'tool' | 'speed' | 'appetite' | 'default';

// Create power-up sprite
export function createPowerUpSprite(type: PowerUpType): Container {
  const container = new Container();

  // Glowing background
  const glow = new Graphics();
  glow.circle(0, 0, 25);

  // Icon
  const icon = new Graphics();

  switch (type) {
    case 'raft':
      glow.fill({ color: 0x00FFFF, alpha: 0.4 });
      // Draw multiple otters holding hands
      icon.ellipse(-10, 0, 8, 5);
      icon.ellipse(0, 0, 8, 5);
      icon.ellipse(10, 0, 8, 5);
      icon.fill(0x8B4513);
      break;

    case 'tool':
      glow.fill({ color: 0xFFD700, alpha: 0.4 });
      // Draw a rock/tool
      icon.ellipse(0, 0, 12, 10);
      icon.fill(0x696969);
      icon.ellipse(2, -2, 4, 3);
      icon.fill(0x888888);
      break;

    case 'speed':
      glow.fill({ color: 0x00FF00, alpha: 0.4 });
      // Draw speed lines
      icon.moveTo(-15, -5);
      icon.lineTo(10, -5);
      icon.lineTo(5, 0);
      icon.lineTo(15, 0);
      icon.lineTo(5, 0);
      icon.lineTo(10, 5);
      icon.lineTo(-15, 5);
      icon.fill(0x32CD32);
      break;

    case 'appetite':
      glow.fill({ color: 0xFF69B4, alpha: 0.4 });
      // Draw x3 multiplier
      const style = new TextStyle({
        fontFamily: 'Arial Black',
        fontSize: 20,
        fill: 0xFF1493,
        fontWeight: 'bold',
      });
      const text = new Text({ text: 'x3', style });
      text.anchor.set(0.5);
      container.addChild(text);
      break;

    default:
      glow.fill({ color: 0xFFFFFF, alpha: 0.4 });
      icon.star(0, 0, 5, 15, 7);
      icon.fill(0xFFD700);
  }

  container.addChild(glow);
  if (type !== 'appetite') {
    container.addChild(icon);
  }

  // Add sparkle effect
  const sparkle = new Graphics();
  sparkle.star(12, -12, 4, 6, 3);
  sparkle.fill(0xFFFFFF);
  container.addChild(sparkle);

  return container;
}

// Legacy functions for backwards compatibility
export function createProceduralOtter(color: number = 0x8B4513): Container {
  return createOtterSprite();
}

export function createProceduralFood(type: string): Container {
  // Return synchronously with a placeholder, then load async
  const container = new Container();

  // Create a temporary colored circle
  const temp = new Graphics();
  const colors: Record<string, number> = {
    fish: 0x4169E1,
    crab: 0xFF6347,
    urchin: 0x800080,
    shrimp: 0xFFA07A,
    clam: 0xD3D3D3,
  };
  temp.circle(0, 0, 12);
  temp.fill(colors[type] || 0xFFD700);
  container.addChild(temp);

  // Async load the real sprite
  createFoodSprite(type as FoodType).then(realSprite => {
    container.removeChild(temp);
    temp.destroy();
    container.addChild(realSprite);
  });

  return container;
}

export function createProceduralObstacle(type: string): Container {
  const container = new Container();

  // Create a temporary shape
  const temp = new Graphics();
  if (type === 'shark') {
    temp.ellipse(0, 0, 35, 15);
    temp.fill(0x708090);
  } else if (type === 'log') {
    temp.roundRect(-30, -10, 60, 20, 5);
    temp.fill(0x8B4513);
  } else if (type === 'net') {
    temp.roundRect(-25, -20, 50, 40, 3);
    temp.fill({ color: 0x228B22, alpha: 0.7 });
  } else if (type === 'pollution') {
    temp.roundRect(-15, -12, 30, 25, 4);
    temp.fill({ color: 0x2F4F4F, alpha: 0.8 });
  } else {
    temp.roundRect(-20, -17, 40, 35, 8);
    temp.fill(0x696969);
  }
  container.addChild(temp);

  // Async load the real sprite
  createObstacleSprite(type as ObstacleType).then(realSprite => {
    container.removeChild(temp);
    temp.destroy();
    container.addChild(realSprite);
  });

  return container;
}

export function createProceduralPowerUp(type: string): Container {
  return createPowerUpSprite(type as PowerUpType);
}

// Clear cached textures
export function clearTextureCache(): void {
  textureCache.clear();
}
